import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Mail as MailIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Message as MessageIcon,
  Public as PublicIcon,
  Article as ArticleIcon,
  ContactMail as ContactMailIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  Category as CategoryIcon,
  BugReport as BugReportIcon,
  MonetizationOn as EarnIcon,
  Payment as PaymentIcon,
  VolunteerActivism as DonationIcon,
  VerifiedUser as VerifiedIcon,
  Shield as ShieldIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { accountService } from '../../services/accountService';
import { getVisibleTabKeys } from '../../services/configService';
import FeedbackDialog from '../FeedbackDialog';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 200; // Reduced from 240

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasAdvancedMailbox, isMinor, minorAllowedTabs } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch account balance for header display
  // Include username in query key so each user gets their own cached balance
  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['accountBalance', user?.username],
    queryFn: () => accountService.getBalance(),
    staleTime: 5000, // Keep header balance fresh
    refetchInterval: 15000, // Refresh frequently for new credits/debits
    refetchOnWindowFocus: true,
    enabled: !!user?.username, // Only fetch if user is logged in
  });

  // Visible tabs config: tabs visible to normal users (admin-configurable). Advanced users see all.
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user?.username,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu items with stable keys for visible-tabs config. Show if hasAdvancedMailbox OR tab is in visible-to-normal list.
  const allMenuItems = [
    { key: 'messages', text: 'Messages', icon: <MessageIcon />, path: '/messages' },
    { key: 'address-book', text: 'Address Book', icon: <ContactMailIcon />, path: '/address-book' },
    { key: 'category-settings', text: 'Category Settings', icon: <CategoryIcon />, path: '/category-settings' },
    { key: 'account-history', text: 'Account History', icon: <HistoryIcon />, path: '/account-history' },
    { key: 'payments', text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
    { key: 'donation-settings', text: 'Donation Settings', icon: <DonationIcon />, path: '/donation-settings' },
    { key: 'earn-credits', text: 'Earn Credits', icon: <EarnIcon />, path: '/earn-credits' },
    { key: 'verification', text: 'Verification', icon: <VerifiedIcon />, path: '/verification' },
    { key: 'my-id-verification-requests', text: 'My ID Verification Requests', icon: <ShieldIcon />, path: '/my-id-verification-requests' },
    { key: 'trust-hub', text: 'Trust Hub', icon: <ShieldIcon />, path: '/trust-hub' },
    { key: 'profile', text: 'My Profile', icon: <AccountCircleIcon />, path: '/profile' },
    { key: 'public-profiles', text: 'Public Profiles', icon: <PublicIcon />, path: '/public-profiles' },
    { key: 'social-posts', text: 'Social Posts', icon: <ArticleIcon />, path: '/social-posts' },
    { key: null, text: 'Support / Report Bug', icon: <BugReportIcon />, path: null, action: 'feedback' as const },
  ];

  // Tabs that minors can never see (regardless of parent settings)
  const MINOR_HIDDEN_TABS = new Set([
    'profile', 'verification', 'trust-hub', 'earn-credits',
    'donation-settings', 'payments', 'my-id-verification-requests',
  ]);

  const menuItems = allMenuItems.filter((item) => {
    if (item.action === 'feedback') return true;
    const key = item.key as string;

    // Minor tab filtering
    if (isMinor) {
      if (MINOR_HIDDEN_TABS.has(key)) return false;
      // If parent has set allowed tabs, enforce that list
      if (minorAllowedTabs !== null && minorAllowedTabs !== undefined) {
        return minorAllowedTabs.includes(key);
      }
      // No explicit list → show all non-hidden tabs
      return true;
    }

    return hasAdvancedMailbox || (visibleTabKeys.length > 0 ? visibleTabKeys.includes(key) : true);
  });

  const handleNavClick = (item: typeof menuItems[0]) => {
    if (item.action === 'feedback') {
      setFeedbackOpen(true);
    } else if (item.path) {
      navigate(item.path);
    }
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.key ?? item.text} disablePadding>
            <ListItemButton
              selected={item.path ? location.pathname === item.path : false}
              onClick={() => handleNavClick(item)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: item.path && location.pathname === item.path ? 'inherit' : 'action.active' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* Hamburger — visible on mobile only */}
          <IconButton
            color="inherit"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, mr: 1 }}
            aria-label="open navigation"
          >
            <MenuIcon />
          </IconButton>

          <MailIcon sx={{ mr: { xs: 0.5, sm: 2 } }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, minWidth: 0 }}>
            MailBox
            {user?.username && (
              <Typography
                component="span"
                variant="subtitle1"
                sx={{ ml: 2, fontWeight: 'normal', opacity: 0.9, display: { xs: 'none', sm: 'inline' } }}
              >
                — {user.username}
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {balanceLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : balance ? (
              <Tooltip title="Click to view account history" arrow>
                <Box component="span" sx={{ display: 'contents' }}>
                  <Typography
                    variant="caption"
                    sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.85, fontWeight: 600 }}
                  >
                    Qix Credits
                  </Typography>
                  {/* Full balance widget — sm and above */}
                  <Box
                    onClick={() => navigate('/account-history')}
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      gap: 3,
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Account Balance
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        ${balance.actualBalance.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Reserved
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', lineHeight: 1.2, color: balance.pendingBalance > 0 ? '#ffcdd2' : 'inherit' }}
                      >
                        ${balance.pendingBalance.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Available
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, color: '#b9f6ca' }}>
                        ${balance.availableBalance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Compact balance widget — xs only */}
                  <Box
                    onClick={() => navigate('/account-history')}
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1, fontSize: '0.6rem' }}>
                        Available
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: '0.75rem', color: '#b9f6ca' }}>
                        ${balance.availableBalance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Tooltip>
            ) : null}

            <IconButton color="inherit" onClick={handleLogout} title="Logout">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 0,
            },
          }}
        >
          {/* Spacer so list starts below AppBar */}
          <Box sx={{ height: 64 }} />
          {drawerContent}
        </Drawer>

        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 64,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: { xs: 1, sm: 2, md: 3 },
            mt: 8,
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        sourcePortal="mailbox-web"
        summaUsername={user?.username}
      />
    </Box>
  );
};

export default Layout;

